import React, {
    useContext,
    useEffect,
    useState,
    useRef,
    SyntheticEvent,
    UIEvent,
} from 'react';
import debounce from 'lodash/debounce';

import {
    TrackingContext,
    TrackingContextValue,
} from '@homeaway/react-event-tracking';
import {CHEVRON_LEFT__16, CHEVRON_RIGHT__16} from '@homeaway/svg-defs';
import {SVGIcon} from '@homeaway/react-svg';
import classnames from 'classnames';

import FilterBarLoading from '../FilterBarLoading/FilterBarLoading';
import FilterGroupPill from '../FilterGroupPill/FilterGroupPill';
import MoreFiltersModal from '../MoreFiltersModal/MoreFiltersModal';
import {FilterGroupType} from '../MoreFilters/MoreFilters';
import {GENERIC_EVENT} from '../../utils/edap';
import {NUM_FILTER_PILLS_DISPLAYED} from '../../utils/constants';

import './FilterBarV2.less';

export const SCROLL_WIDTH = 300;

const isDropdown = (target: HTMLElement) =>
    target && target.className && target.className.includes('Dropdown');

export type CustomFilterParams = {
    selectedFilterByTotalPrice: boolean;
    appliedFilterByTotalPrice: boolean;
    selectedMaxPrice: number | null;
    selectedMinPrice: number | null;
    appliedMaxPrice: number | null;
    appliedMinPrice: number | null;
    selectedMinBedrooms: number;
    selectedMinBathrooms: number;
    appliedMinBedrooms: number;
    appliedMinBathrooms: number;
};

export interface Props {
    appliedFilters: string[];
    currency: string;
    customFiltersParams: CustomFilterParams;
    displayNumGroupsAsPills?: number;
    filterGroups: FilterGroupType[];
    isMobile: boolean;
    onClear: (filters: string[]) => void;
    onCustomFilterUpdate: (filters: any) => void;
    onDismiss: () => void;
    onDone: () => void;
    onFilterToggle: (event: SyntheticEvent<HTMLInputElement>) => void;
    onMultipleFilterToggle: Function;
    priceFilterOptions?: {
        isFilterByTotalPriceEnabled?: boolean;
        lengthOfStay?: number;
        showFilterByPriceType?: boolean;
    };
    priceInputValuesAreValid: boolean;
    results: number;
    selectedFilters: string[];
    updatePriceInputValuesValidity: (valid: boolean) => void;
}

// eslint-disable-next-line complexity
export default function FilterBarV2({
    appliedFilters,
    currency,
    customFiltersParams,
    displayNumGroupsAsPills = NUM_FILTER_PILLS_DISPLAYED,
    filterGroups = [],
    isMobile,
    onClear,
    onCustomFilterUpdate,
    onDismiss,
    onDone,
    onFilterToggle,
    onMultipleFilterToggle,
    priceFilterOptions = {},
    priceInputValuesAreValid,
    results,
    selectedFilters,
    updatePriceInputValuesValidity,
}: Props) {
    const edapContext: TrackingContextValue = useContext(TrackingContext);
    const filterBarContentRef = useRef<HTMLDivElement>(null);
    const [isHover, setHover] = useState(false);
    const [offsetLeft, setOffsetLeft] = useState(0);
    const [isScrolling, setScroll] = useState(false);
    const isLoading = filterGroups.length === 0;

    useEffect(() => {
        const eventFields = {
            eventaction: 'filter bar presented',
        };
        edapContext.trigger(GENERIC_EVENT, eventFields);
    }, [edapContext]);

    const topFilterGroups = filterGroups.slice(0, displayNumGroupsAsPills);

    const hoverInit = () => {
        const node = filterBarContentRef.current;
        if (node) {
            setOffsetLeft(node.scrollLeft);
            if (node.scrollWidth > window.innerWidth) {
                setHover(true);
            }
        }
    };

    const handlePaginationClick = (dir: string) => {
        const node = filterBarContentRef.current;
        if (node) {
            const newScrollLeft =
                dir === 'left'
                    ? node.scrollLeft - SCROLL_WIDTH
                    : node.scrollLeft + SCROLL_WIDTH;

            // browser supports native smoothscroll
            if ('scrollBehavior' in document.documentElement.style) {
                node.scrollTo({
                    left: newScrollLeft,
                    top: 0,
                    behavior: 'smooth',
                });
            } else {
                node.scrollLeft = newScrollLeft;
            }
            setOffsetLeft(newScrollLeft);
        }
    };

    const handleOnMouseLeave = () => {
        setHover(false);
    };

    const handleOnBlur = () => {
        setHover(false);
    };

    const handleScrollDebounced = debounce(
        () => {
            if (isScrolling) {
                setScroll(false);
            } else {
                setScroll(true);
            }
        },
        100,
        {leading: !isScrolling, trailing: isScrolling}
    );

    const handleScroll = (event: UIEvent<HTMLElement>) => {
        const target = event.target as HTMLElement;
        if (isMobile || isDropdown(target)) {
            return;
        }

        handleScrollDebounced();
    };

    const sharedProps = {
        appliedFilters,
        customFiltersParams,
        currency,
        isMobile,
        onClear,
        onCustomFilterUpdate,
        onDismiss,
        onDone,
        onFilterToggle,
        onMultipleFilterToggle,
        priceFilterOptions,
        priceInputValuesAreValid,
        selectedFilters,
        updatePriceInputValuesValidity,
        results,
    };

    const filtersPaginationClassnames = classnames('FilterBarV2__pagination', {
        'FilterBarV2__pagination--hover': isHover,
        'FilterBarV2__pagination--bound-left': offsetLeft <= 0,
        'FilterBarV2__pagination--bound-right':
            filterBarContentRef.current &&
            offsetLeft + filterBarContentRef.current.offsetWidth >=
                filterBarContentRef.current.scrollWidth,
    });

    return (
        <div className="FilterBarV2">
            <div
                className="FilterBarV2__content"
                ref={filterBarContentRef}
                onMouseEnter={hoverInit}
                onMouseLeave={handleOnMouseLeave}
                onBlur={handleOnBlur}
                onScroll={handleScroll}
                role="presentation"
            >
                {isLoading ? (
                    <FilterBarLoading
                        placeholdersCount={displayNumGroupsAsPills + 1}
                    />
                ) : (
                    <>
                        {topFilterGroups.map(({groupInfo, filters}) => (
                            <FilterGroupPill
                                key={groupInfo.id}
                                label={groupInfo.name}
                                secondaryLabel={''} // TODO: Get this key from filter service
                                groupId={groupInfo.id}
                                filters={filters}
                                scrolling={isScrolling}
                                {...sharedProps}
                            />
                        ))}
                        <MoreFiltersModal
                            filterGroups={filterGroups}
                            sliceAt={displayNumGroupsAsPills}
                            {...sharedProps}
                        />
                    </>
                )}

                <div
                    className={`${filtersPaginationClassnames} FilterBarV2__pagination--left`}
                    onClick={() => {
                        handlePaginationClick('left');
                    }}
                    role="button"
                    aria-hidden
                >
                    <SVGIcon markup={CHEVRON_LEFT__16} />
                </div>

                <div
                    className={`${filtersPaginationClassnames} FilterBarV2__pagination--right`}
                    onClick={() => {
                        handlePaginationClick('right');
                    }}
                    role="button"
                    aria-hidden
                >
                    <SVGIcon markup={CHEVRON_RIGHT__16} />
                </div>
            </div>
        </div>
    );
}
